<script setup>
import { reactive, ref, computed } from 'vue'
import FaceImageSlots from './FaceImageSlots.vue'
import { createEmptyImages } from '../utils/faceImages.js'

const props = defineProps({
    initialData: Object
})
const emit = defineEmits(['goConfirm', 'close'])

const reactiveObj = reactive({
    id: props.initialData?.id || null,
    userId: props.initialData?.userId || '',
    userName: props.initialData?.userName || '',
    images: props.initialData?.images || createEmptyImages()
})
const userInfos = ref(
    props.initialData?.userInfos?.length
        ? JSON.parse(JSON.stringify(props.initialData.userInfos))
        : [{ name: '', content:'', placeholder1: '例）メールアドレス', placeholder2: '例）example@example.com'}]
)

// 画像が1枚でも選択されているか
const hasImage = computed(() => {
    return reactiveObj.images.some(image => image.file)
})

const isValid = computed(() => {
    // 画像を更新する場合、画像1は必須
    if (hasImage.value && !reactiveObj.images[0].file) {
        return false
    }
    return (
        !!reactiveObj.userId ||
        !!reactiveObj.userName ||
        hasImage.value ||
        userInfos.value.some(info => info.name || info.content)
    )
})

// 項目欄 追加
const addUserInfo = () => {
    userInfos.value.push({
        name: '',
        content:'',
        placeholder1: '入力してください',
        placeholder2: '入力してください'
    })
}

// 項目欄 削除
const deleteUserInfo = (index) => {
    userInfos.value.splice(index, 1)
}

// 確認画面へ遷移
const goConfirm = () => {
    emit('goConfirm', {
        id: reactiveObj.id,
        userId: reactiveObj.userId,
        userName: reactiveObj.userName,
        images: reactiveObj.images,
        userInfos: userInfos.value
    })
}
// 新規登録処理をキャンセル
const closeRegist = () => {
    emit('close')
}
</script>

<template>
    <div class="mainView">
        <nav class="breadcrumb">
            <span class="breadcrumb-sub" @click="closeRegist">利用者管理 </span>
            <span> &gt; </span>
            <span class="breadcrumb-main"> 利用者情報更新</span>
        </nav>
        <div class="titleArea">
            <p class="title">利用者情報更新</p>
            <div class="titleTextArea">
                <p class="titleText">登録済みの利用者情報を編集します。必要に応じて基本情報や顔画像を変更してください。</p>
            </div>
        </div>
        <div class="userRegistArea">
            <div class="inputUserInfoArea">
                <p class="registUserTitle" ><span class="fontColorPk">① </span>基本情報</p>
                <div class="formRow">
                    <p class="label">ユーザーID </p>
                    <div class="inputArea">
                        <input type="text" placeholder="例) user-0001" v-model="reactiveObj.userId">
                        <p class="note">半角英数字、ハイフン（-）、アンダースコア（_）が使用できます。</p>
                    </div>
                </div>
                <div class="formRow">
                    <p class="label">ユーザー名 </p>
                    <div class="inputArea">
                        <input type="text" placeholder="例) 山田 太郎" v-model="reactiveObj.userName">
                        <p class="note">ユーザーの氏名または表示名を入力してください。</p>
                    </div>
                </div>
                <table class="userInfoTable">
                    <thead>
                        <tr>
                            <th>項目</th>
                            <th>内容</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(userInfo, index) in userInfos" :key="index">
                            <td>
                                <input class="inputUsrInfo" v-model="userInfo.name" :placeholder="userInfo.placeholder1">
                            </td>
                            <td>
                                <input class="inputUsrInfo" v-model="userInfo.content" :placeholder="userInfo.placeholder2">
                            </td>
                            <td>
                                <button class="deleteButton" @click="deleteUserInfo(index)"><img src="../assets/usermng_delete.png" class="deleteIcon"></button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div id="addUserArea">
                    <button class="exeButton textLeft" @click="addUserInfo">＋ 項目を追加</button>
                </div>
                <p class="textLeft note">※ 上記は任意項目です。必要に応じて項目を追加してください。</p>
            </div>
            <div class="inputUserImgArea">
                <p class="registUserTitle" ><span class="fontColorPk">② </span>顔画像登録 <span class="maxImageBadge">最大3枚</span></p>
                <p class="textLeft imgDescription">顔画像を1〜3枚登録できます。複数の顔画像を登録することで、認証精度の向上が期待できます。</p>
                <p class="textLeft imgOverwriteNote">※ 画像を更新すると、以前に登録した画像に上書きされます。</p>
                <p v-if="hasImage && !reactiveObj.images[0].file" class="textLeft imgOverwriteNote">※ 画像を更新する場合は、画像1を選択してください。</p>
                <FaceImageSlots v-model="reactiveObj.images" />
            </div>
        </div>
        <div class="userRegistButtonArea">
            <button type="button" class="clearButton" @click="closeRegist">キャンセル</button>
            <button type="button" class="nextButton" @click="goConfirm" :disabled="!isValid">確認画面へ</button>
        </div>
    </div>
</template>

<style scoped>
    .breadcrumb{
        align-items: left;
        text-align: left;
        padding-top:15px;
        padding-left:20px;
    }
    .breadcrumb-sub{
        font-size: 18px;
        margin: 0 10px;
        cursor:pointer;
    }
    .breadcrumb-main{
        font-size: 18px;
        margin: 0 10px;
        font-weight: bold;
    }
    .userRegistArea {
        width:95%;
        margin: 0 30px;
        border-radius: 10px;
        border: 1px solid #e0e0e0;
        padding:20px;
    }
    .inputUserInfoArea{
        width:100%;
        border-bottom: 1px solid #cecbcb;
        padding:15px;
    }
    .registUserTitle {
        font-size: 20px;
        font-weight: bold;
        text-align: left;
        padding:10px;
    }
    .formRow {
        display: flex;
        align-items: flex-start;
        gap: 20px;
        margin-bottom: 20px;
    }
    .inputArea {
        display: flex;
        flex-direction: column;
        margin-top: 10px;
    }
    .inputArea input {
        width: 90%;
        padding: 8px;
        border-radius: 10px;
        margin:0 10px;
        height:40px;
    }
    .note {
        font-size: 15px;
        color: #777;
        margin-top: 5px;
    }
    .label {
        width: 150px;
        text-align: right;
        margin-top: 8px;
        font-weight: bold;
        padding:10px;
    }
    .userInfoTable {
        width: 100%;
        border-collapse: separate;
    }
    .userInfoTable thead th {
        background: #eae6e7;
        padding: 12px;
        text-align: left;
        font-weight: bold;
    }
    .userInfoTable tbody tr {
        background: #fff;
        border-radius: 8px;
        margin-bottom: 10px;
    }
    .userInfoTable tbody td {
        padding: 10px 15px;
        vertical-align: middle;
    }
    .userInfoTable thead tr th:first-child {
        border-top-left-radius: 8px;
    }
    .userInfoTable thead tr th:last-child {
        border-top-right-radius: 8px;
    }
    #addUserArea{
        border-radius: 10px;
        border: 1px solid #e0e0e0;
        background-color: white;
        padding:10px;
        text-align: left;
        margin: 10px 0 10px 0;
    }
    .inputUserImgArea {
        display: block;
        gap: 40px;
        justify-content: left;
        margin-top: 20px;
    }
    .userRegistButtonArea {
        display: flex;
        justify-content: center;
        margin:20px;
        gap:20px;
    }
    .inputUsrInfo {
        width: 90%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        text-align: left;
    }
    .deleteButton {
        width: 45px;
        height: 45px;
        border: 1px solid #ccc;
        background: #fff;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }
    .deleteIcon{
        width:30px;
        height: 30px;
    }

    @media (max-width: 1024px) {
        .userRegistArea {
            width: 100%;
            margin: 0;
            padding: 15px;
            box-sizing: border-box;
        }
        .inputUserInfoArea,
        .inputUserImgArea {
            width: 100%;
            padding: 10px;
            box-sizing: border-box;
        }
        .formRow {
            flex-direction: column;
            gap: 8px;
            margin-bottom: 15px;
        }
        .label {
            width: 100%;
            text-align: left;
            margin: 0;
            padding: 0;
        }
        .inputArea {
            width: 100%;
        }
        .inputArea input {
            width: 100%;
            margin: 0;
            box-sizing: border-box;
        }
        .userRegistButtonArea {
            flex-direction: column;
            gap: 10px;
        }
        .clearButton,
        .nextButton {
            width: 100%;
        }
    }   

    @media (max-width: 768px) {
        *, *::before, *::after {
            box-sizing: border-box;
        }
        .breadcrumb{
            margin-top: 15px;
            padding:10px;
            font-size:14px;
        }
        .breadcrumb-sub, .breadcrumb-main{
            font-size:16px;
            margin:0 3px;
        }
        .formRow {
            gap: 10px;
        }
        .inputArea input {
            font-size: 0.96rem;
        }
        .userInfoTable,
        .userInfoTable tbody,
        .userInfoTable tr,
        .userInfoTable td{
            display:block;
            width:100%;
        }
        .userInfoTable thead{
            display:none;
        }
        .userInfoTable tr{
            margin-bottom:12px;
            padding:12px;
            border:1px solid #eee;
            border-radius:8px;
            background:#fff;
        }
        .userInfoTable td{
            padding:8px 0;
        }
        .inputUsrInfo{
            width:100%;
        }
        .deleteButton{
            margin-top:8px;
        }
    }
</style>
